import { Injectable } from '@angular/core';
import {
	CAPACITY_SEARCH_FILTER,
	CARGOCHIEF_KEYS,
	RANGE_FILTERS,
} from '@domain/constants/app-variables';
import {
	CarrierDTOResponse,
	TruckSearchTable,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
} from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '../cargochief/cargochief.service';
import { AccountSettingsService } from '../accountSettings/account-settings.service';
import { environment } from '@environment/environment';

@Injectable({
	providedIn: 'root',
})
export class CapacitySearchFilterService {
	constructor(
		public utilService: UtilService,
		private cargochief: Cargochief,
		private accountSettings: AccountSettingsService
	) {}

	applyFilter(
		carrierSelected: any,
		truckFiltersSelected: any,
		truckSearchResult: Array<TruckSearchTable>,
		sortColumn: string,
		carrierHQList: Array<CarrierDTOResponse>
	) {
		truckSearchResult = [...this.applyCarrierSelectedFilter(carrierSelected, truckSearchResult)];
		if (truckFiltersSelected.length > 0) {
			truckSearchResult = [...this.applyTruckFilters(truckFiltersSelected, truckSearchResult)];
		}
		if (!this.utilService.isNullOrUndefined(carrierHQList) && carrierHQList.length > 0) {
			let result: Array<TruckSearchTable> = [];
			carrierHQList.map((carrier) => {
				const search: TruckSearchTable = truckSearchResult.filter((csearch) => {
					return csearch.carrierId == carrier.id;
				})[0];
				if (!this.utilService.isNullOrUndefined(search)) {
					result.push(search);
				}
			});
			truckSearchResult = [...result];
		}
		return this.sortByColumn(sortColumn, truckSearchResult);
	}

	applyCarrierSelectedFilter(
		carrierSelected,
		filteredResult: Array<TruckSearchTable>
	): Array<TruckSearchTable> {
		switch (carrierSelected) {
			case CAPACITY_SEARCH_FILTER.CARRIERS[0]:
				return filteredResult;
				break;
			case CAPACITY_SEARCH_FILTER.CARRIERS[1]:
				return filteredResult.filter((search: TruckSearchTable) => {
					if (search.customerCarrier) {
						return search;
					}
				});
			default:
				return filteredResult;
		}
	}

	getMinDHD(truckSearchResult: Array<TruckSearchTable>): number {
		let result = null;
		truckSearchResult.map((search: TruckSearchTable) => {
			if (!this.utilService.isNullOrUndefined(search.destinationDeadhead)) {
				if (result == null) {
					result = Math.round(search.destinationDeadhead);
				} else if (Math.round(search.destinationDeadhead) < result) {
					result = Math.round(search.destinationDeadhead);
				}
			}
		});
		return result;
	}

	getMaxMatch(truckSearchResult: Array<TruckSearchTable>): number {
		let result = null;
		truckSearchResult.map((search: TruckSearchTable) => {
			if (!this.utilService.isNullOrUndefined(search.match)) {
				if (result == null) {
					result = Math.round(search.match);
				} else if (Math.round(search.match) > result) {
					result = Math.round(search.match);
				}
			}
		});
		return result;
	}

	/**
	 * This matches the Match Confidence display's color
	 * @param matchScore the score 0-100 to group into buckets that match the display color.
	 */
	getBucketedMatchScore(matchScore) {
		if (matchScore > 66) {
			return 2;
		} else if (matchScore > 33) {
			return 1;
		} else {
			return 0;
		}
	}

	/**
	 * This matches the values that are used in displaying Quality Score
	 * @param ccScore the score 0-100 to group into buckets that match the score text.
	 */
	getBucketedCCScore(ccScore) {
		if (ccScore > environment.HIGH_QUALITY) {
			return 2;
		} else if (ccScore > environment.MEDIUM_QUALITY) {
			return 1;
		} else {
			return 0;
		}
	}

	sortSearchResultByMatch(truckSearchResult: Array<TruckSearchTable>): Array<TruckSearchTable> {
		return truckSearchResult.sort(
			(a, b) =>
				(b.match || 0) - (a.match || 0) ||  // Primary: actual match score (descending)
				(b.ccScore || 0) - (a.ccScore || 0) ||  // Secondary: actual quality score (descending)
				a.businessName?.localeCompare(b.businessName)  // Tertiary: alphabetical
		);
	}

	sortSearchResultByDistance(truckSearchResult: Array<TruckSearchTable>): Array<TruckSearchTable> {
		return truckSearchResult.sort(
			(a, b) => a.distance - b.distance || a.businessName?.localeCompare(b?.businessName)
		);
	}

	sortSearchResultDHD(truckSearchResult: Array<TruckSearchTable>): Array<TruckSearchTable> {
		let withdhd = [],
			withoutdhd = [];
		truckSearchResult.map((search) => {
			if (this.utilService.isNullOrUndefined(search.destinationDeadhead)) {
				withoutdhd.push(search);
			} else {
				if (search.isLane) {
					const lp = search?.lanePath ?? '';
					const last = lp.substring(lp.lastIndexOf(' → ') + 3);
					if (last.length > 2) {
						withdhd.push(search);
					} else {
						withoutdhd.push(search);
					}
				} else if (!search.isLane) {
					withdhd.push(search);
				}
			}
		});
		withdhd = withdhd.sort(
			(a, b) =>
				b.destinationDeadhead - a.destinationDeadhead ||
				a.businessName?.localeCompare(b?.businessName)
		);
		return withdhd.concat(withoutdhd);
	}

	getMaxDHO(truckSearchResult: Array<TruckSearchTable>): number {
		let result = null;
		truckSearchResult.map((search: TruckSearchTable) => {
			if (!this.utilService.isNullOrUndefined(search.originDeadhead)) {
				if (result == null) {
					result = Math.round(search.originDeadhead);
				} else if (Math.round(search.originDeadhead) > result) {
					result = Math.round(search.originDeadhead);
				}
			}
		});
		return result;
	}

	getMaxDHD(truckSearchResult: Array<TruckSearchTable>): number {
		let result = null;
		truckSearchResult.map((search: TruckSearchTable) => {
			if (!this.utilService.isNullOrUndefined(search.destinationDeadhead)) {
				if (result == null) {
					result = Math.round(search.destinationDeadhead);
				} else if (Math.round(search.destinationDeadhead) > result) {
					result = Math.round(search.destinationDeadhead);
				}
			}
		});
		return result;
	}

	sortSearchResultByFrequency(truckSearchResult: Array<TruckSearchTable>): Array<TruckSearchTable> {
		const newSearch = truckSearchResult.map(({ businessName, frequency, match, ccScore }, i) => {
			let formattedFrequency = frequency === 'Daily' ? 7 : 0;

			if (!formattedFrequency && !isNaN(Number(frequency))) {
				formattedFrequency = Number(frequency);
			}

			if (!formattedFrequency) {
				formattedFrequency = frequency.split(',').length;
			}

			return { index: i, frequency: formattedFrequency, businessName, match, ccScore };
		});

		return newSearch
			.sort((a, b) => {
				return (
					b.frequency - a.frequency ||
					(b.ccScore || 0) - (a.ccScore || 0) ||  // Use actual quality score
					(b.match || 0) - (a.match || 0) ||  // Use actual match score
					a.businessName?.localeCompare(b?.businessName)
				);
			})
			.map(({ index }) => truckSearchResult[index]);
	}

	sortByColumn(
		sortColumn: string,
		truckSearchResult: Array<TruckSearchTable>
	): Array<TruckSearchTable> {
		switch (sortColumn) {
			case RANGE_FILTERS.SORT.MATCH:
				return this.sortSearchResultByMatch(truckSearchResult);
			case RANGE_FILTERS.SORT.FREQUENCY:
				return this.sortSearchResultByFrequency(truckSearchResult);
			case RANGE_FILTERS.SORT.DISTANCE_FROM_HQ:
				return this.sortSearchResultByDistance(truckSearchResult);
			case RANGE_FILTERS.SORT.QUALITY:
				return this.sortSearchResultByQuality(truckSearchResult);
			default:
				return truckSearchResult;
		}
	}

	applyTruckFilters(
		truckFiltersSelected,
		filteredResult: Array<TruckSearchTable>
	): Array<TruckSearchTable> {
		switch (truckFiltersSelected) {
			case CAPACITY_SEARCH_FILTER.TRUCKS[1]:
				return filteredResult.filter((search: TruckSearchTable) => {
					if (!search.isLane) {
						return search;
					}
				});
			default:
				return filteredResult;
		}
	}

	getDeadhead(): { originDeadhead: number; destinationDeadhead: number } {
		const result = { originDeadhead: null, destinationDeadhead: null };
		const DHO = this.cargochief.getItem(CARGOCHIEF_KEYS.ORIGIN_DEADHEAD_SLIDER),
			DHD = this.cargochief.getItem(CARGOCHIEF_KEYS.DESTINATION_DEADHEAD_SLIDER);
		if (!this.utilService.isNullOrUndefined(DHO)) {
			result.originDeadhead = JSON.parse(DHO);
		} else {
			result.originDeadhead = this.accountSettings.SEARCH_TRUCK_ORIGIN_DEADHEAD_LIMIT;
		}
		if (!this.utilService.isNullOrUndefined(DHD)) {
			result.destinationDeadhead = JSON.parse(DHD);
		} else {
			result.destinationDeadhead = this.accountSettings.SEARCH_TRUCK_DESTINATION_DEADHEAD_LIMIT;
		}
		return result;
	}

	getMinLoadsPerWeek(): number {
		const frequencyFilter = this.cargochief.getItem(CARGOCHIEF_KEYS.FREQUENCY_FILTER);
		if (IS_NOT_NULL_OR_UNDEFINED(frequencyFilter)) {
			const { minLoadsPerWeek } = JSON.parse(frequencyFilter);
			return minLoadsPerWeek;
		}
		return null;
	}

	sortSearchResultByQuality(truckSearchResult: Array<TruckSearchTable>): Array<TruckSearchTable> {
		let highQualitySearchResults = [],
			mediumQualitySearchResults = [],
			lowQualitySearchResults = [],
			noQualitySearchResult = [];
		truckSearchResult.forEach((searchResult) => {
			if (IS_NULL_OR_UNDEFINED(searchResult.ccScore)) {
				noQualitySearchResult.push(searchResult);
			} else {
				if (searchResult.ccScore > environment.HIGH_QUALITY) {
					highQualitySearchResults.push(searchResult);
				} else if (searchResult.ccScore > environment.MEDIUM_QUALITY) {
					mediumQualitySearchResults.push(searchResult);
				} else {
					lowQualitySearchResults.push(searchResult);
				}
			}
		});

		return [
			...highQualitySearchResults.sort(
				(a, b) =>
					(b.ccScore || 0) - (a.ccScore || 0) ||  // Within high quality, sort by actual quality score
					(b.match || 0) - (a.match || 0) ||  // Then by actual match score
					a.businessName?.localeCompare(b.businessName)
			),
			...mediumQualitySearchResults.sort(
				(a, b) =>
					(b.ccScore || 0) - (a.ccScore || 0) ||  // Within medium quality, sort by actual quality score
					(b.match || 0) - (a.match || 0) ||  // Then by actual match score
					a.businessName?.localeCompare(b.businessName)
			),
			...lowQualitySearchResults.sort(
				(a, b) =>
					(b.ccScore || 0) - (a.ccScore || 0) ||  // Within low quality, sort by actual quality score
					(b.match || 0) - (a.match || 0) ||  // Then by actual match score
					a.businessName?.localeCompare(b.businessName)
			),
			...noQualitySearchResult.sort(
				(a, b) =>
					(b.match || 0) - (a.match || 0) ||  // No quality score, sort by actual match score
					a.businessName?.localeCompare(b.businessName)
			),
		];
	}

	getAppliedFilterNames(model: any, defaults: any): string[] {
		const names: string[] = [];
		const numberChanged = (current: any, base: any): boolean => {
			const a = Number(current);
			const b = Number(base);
			if (isNaN(a) && isNaN(b)) {
				return false;
			}
			return a !== b;
		};
		const deadheadChanged =
			(IS_NOT_NULL_OR_UNDEFINED(model.originDeadhead) &&
				numberChanged(model.originDeadhead, defaults.originDeadhead)) ||
			(IS_NOT_NULL_OR_UNDEFINED(model.destinationDeadhead) &&
				numberChanged(model.destinationDeadhead, defaults.destinationDeadhead));
		if (deadheadChanged) {
			names.push('Deadhead');
		}
		if (numberChanged(model.powerUnits, defaults.powerUnits)) {
			names.push('Min Truck Count');
		}
		if (numberChanged(model.minLoadsPerWeek, defaults.minLoadsPerWeek)) {
			names.push('Min Frequency');
		}
		if (model.qualityFilter) {
			names.push('Quality');
		}
		if (model.ratesOnly) {
			names.push('Has Rate');
		}
		if (model.carriersSearchText && model.carriersSearchText.trim()) {
			names.push('MC/DOT');
		}
		if (model.carrierHQ) {
			names.push('Carrier HQ');
		}
		if (model.carrierSelected && model.carrierSelected !== CAPACITY_SEARCH_FILTER.CARRIERS[0]) {
			names.push('Carriers');
		}
		return names;
	}

	getFilterIndicator(model: any, defaults: any): string {
		const names = this.getAppliedFilterNames(model, defaults);
		if (!names.length) {
			return '';
		}
		return ' (' + (names.length > 1 ? names.length : names[0]) + ')';
	}
}
